import { createContext, useContext, useState, ReactNode } from 'react';
import { User, MockUserProfile, UserRole, StatusType } from '@/types/user';

// Mock user data for development
const createMockUser = (): User => ({
  id: 'user-001',
  email: 'sarah.chen@line4demo.com',
  firstName: 'Sarah',
  lastName: 'Chen',
  role: 'risk_manager' as UserRole,
  department: 'Risk Management',
  organization: 'Line4 Demo Corp',
  status: 'active' as StatusType,
  lastLogin: new Date('2024-11-05T09:30:00Z'),
  createdAt: new Date('2024-01-15T10:00:00Z'),
  updatedAt: new Date('2024-11-05T09:30:00Z'),
  permissions: [
    {
      id: 'perm-001',
      resource: 'risk_registers',
      actions: ['read', 'write', 'approve']
    },
    {
      id: 'perm-002',
      resource: 'compliance_frameworks',
      actions: ['read', 'write']
    },
    {
      id: 'perm-003',
      resource: 'dashboards',
      actions: ['read', 'write', 'export']
    },
    {
      id: 'perm-004',
      resource: 'policies',
      actions: ['read', 'approve']
    }
  ]
});

const createMockUserProfile = (): MockUserProfile => ({
  user: createMockUser(),
  preferences: {
    theme: 'dark',
    dashboardLayout: 'detailed',
    notifications: {
      email: true,
      inApp: true,
      frequency: 'immediate'
    },
    timezone: 'UTC'
  },
  recentActivity: [
    {
      id: 'activity-001',
      action: 'Updated risk assessment',
      resource: 'Risk Register: Cyber Security Framework',
      timestamp: new Date('2024-11-05T08:45:00Z'),
      details: 'Increased likelihood from Medium to High'
    },
    {
      id: 'activity-002',
      action: 'Approved control testing',
      resource: 'Control: Multi-Factor Authentication',
      timestamp: new Date('2024-11-05T07:30:00Z'),
      details: 'Marked as effective for Q4 2024'
    },
    {
      id: 'activity-003',
      action: 'Reviewed compliance status',
      resource: 'DORA Framework Compliance',
      timestamp: new Date('2024-11-04T16:15:00Z'),
      details: 'Updated 3 requirements to compliant status'
    }
  ]
});

interface MockUserContextType {
  userProfile: MockUserProfile;
  updateUserPreferences: (preferences: Partial<MockUserProfile['preferences']>) => void;
  hasPermission: (resource: string, action: string) => boolean;
  isLoading: boolean;
}

const MockUserContext = createContext<MockUserContextType | undefined>(undefined);

interface MockUserProviderProps {
  children: ReactNode;
}

export function MockUserProvider({ children }: MockUserProviderProps) {
  const [userProfile, setUserProfile] = useState<MockUserProfile>(createMockUserProfile());
  const [isLoading] = useState(false);

  const updateUserPreferences = (preferences: Partial<MockUserProfile['preferences']>) => {
    setUserProfile((prev: MockUserProfile) => ({
      ...prev,
      preferences: { ...prev.preferences, ...preferences }
    }));
  };

  const hasPermission = (resource: string, action: string): boolean => {
    return userProfile.user.permissions.some((permission) => 
      permission.resource === resource && permission.actions.includes(action as any)
    );
  };

  const value: MockUserContextType = {
    userProfile,
    updateUserPreferences,
    hasPermission,
    isLoading
  };

  return (
    <MockUserContext.Provider value={value}>
      {children}
    </MockUserContext.Provider>
  );
}

export function useMockUser() {
  const context = useContext(MockUserContext);
  if (context === undefined) {
    throw new Error('useMockUser must be used within a MockUserProvider');
  }
  return context;
}

// Utility functions for role-based features
export function getRoleDisplayName(role: UserRole): string {
  const roleNames: Record<UserRole, string> = {
    risk_manager: 'Risk Manager',
    compliance_officer: 'Compliance Officer',
    admin: 'Administrator',
    viewer: 'Viewer'
  };
  return roleNames[role];
}

export function getRoleColor(role: UserRole): string {
  const roleColors: Record<UserRole, string> = {
    risk_manager: 'badge-primary',
    compliance_officer: 'badge-success',
    admin: 'badge-warning',
    viewer: 'badge-secondary'
  };
  return roleColors[role];
}